import AlertMessage from '@/components/AlertMessage';
import {
  InputField,
  NumberField,
  TextareaField,
} from '@/components/FormElements';
import PaymentMethod from '@/components/PaymentMethod';
import ShareDialog from '@/components/ShareDialog';
import { Button } from '@/components/ui/button';
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import Typography from '@/components/ui/typography';
import Band from '@/Layouts/Band';
import SectionLayout from '@/Layouts/SectionLayout';
import { calculatePercentOff, cn } from '@/lib/utils';
import { PageProps, ProductVariant, ShoppeProduct } from '@/types';
import { yupResolver } from '@hookform/resolvers/yup';
import { Link, router, usePage } from '@inertiajs/react';
import { AxiosError } from 'axios';
import { Boxes, ShieldCheck, Truck } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import * as yup from 'yup';

type Product = ShoppeProduct<ProductVariant>;

const InventoryStatus = ({
  inventory,
}: {
  inventory: ProductVariant['inventory'];
}) => {
  const qty = inventory[0].available_quantity;

  return qty > 0 && qty < 5 ? (
    <Typography.Body className="font-medium">Only {qty} left</Typography.Body>
  ) : null;
};

const OrderSchema = yup.object({
  address: yup.string().required('Address is required'),
  pin_code: yup
    .string()
    .required('Pincode is required')
    // Allow only numbers
    .matches(/^[0-9]+$/, 'Pincode must only contain numbers')
    .length(6, 'Pincode must be 6 digits long'),
  qty: yup.number().default(1).min(1),
  amount: yup.number().defined(),
  discount_code: yup.string().defined(),
  discount: yup.number().default(0),
  final_price: yup.number().defined(),
  shoppe_product_inventory_id: yup.number(),
  transaction_no: yup.string().required('Transaction Number is required'),
});

type OrderFormData = yup.InferType<typeof OrderSchema>;

interface DiscountResponse {
  message: string;
  type: 'percentage' | 'fixed';
  value: number;
  min_order_amount: number;
  max_discount: number;
}

const ProductPayment = ({
  open,
  onClose,
  onResult,
  productDetails,
}: {
  open: boolean;
  onClose: (open: boolean) => void;
  onResult: (data: { status: 'success' | 'error'; message: string }) => void;
  productDetails: {
    amount: number;
    available_qty: number;
    inventory_id: number;
  };
}) => {
  const form = useForm<OrderFormData>({
    resolver: yupResolver(OrderSchema),
    defaultValues: {
      address: '',
      pin_code: '',
      qty: 1,
      amount: productDetails.amount,
      discount_code: '',
      discount: 0,
      final_price: productDetails.amount,
      transaction_no: '',
      shoppe_product_inventory_id: productDetails.inventory_id,
    },
  });
  const discount = useRef<DiscountResponse | undefined>(undefined);

  // destructure form state to subscribe to proxy form state
  const { isSubmitting } = form.formState;

  const [qtyField, discountCode, beforeDiscount, afterDiscount] = form.watch([
    'qty',
    'discount_code',
    'amount',
    'final_price',
  ]);

  const calculateDiscount = useCallback(() => {
    const [totalAmount, discountValue] = form.getValues(['amount', 'discount']);
    let discountedAmount = discountValue;

    if (discount.current) {
      const { min_order_amount, type, value, max_discount } = discount.current;

      if (totalAmount < min_order_amount) {
        form.setValue('discount', 0);
        form.setValue('amount', totalAmount);
        form.setValue('final_price', totalAmount);

        throw new Error(
          'This code is applicable only for Minimum order amount of Rs.' +
            min_order_amount
        );
      } else {
        form.clearErrors('discount_code');
      }

      if (type === 'percentage') {
        const percent = value / 100;
        discountedAmount = totalAmount * percent;

        if (max_discount !== 0 && discountedAmount > max_discount) {
          discountedAmount = max_discount;
        }
      } else {
        discountedAmount = value;
      }
    }

    form.setValue('discount', discountedAmount);
    form.setValue('amount', totalAmount);
    form.setValue('final_price', Math.floor(totalAmount - discountedAmount));
  }, [form]);

  useEffect(() => {
    form.setValue('amount', productDetails.amount * qtyField);
    try {
      calculateDiscount();
    } catch (error) {
      form.setError('discount_code', {
        type: 'server',
        message: (error as Error)?.message,
      });
    }
  }, [calculateDiscount, form, productDetails.amount, qtyField]);

  useEffect(() => {
    form.setValue('shoppe_product_inventory_id', productDetails.inventory_id);
  }, [form, productDetails.inventory_id]);

  const handleDiscount = async () => {
    try {
      const response = await window.axios.post('/api/discount-codes/apply', {
        code: discountCode,
      });
      discount.current = response.data as DiscountResponse;

      calculateDiscount();

      form.clearErrors('discount_code');

      toast.success(discount.current.message);
    } catch (error) {
      form.setValue('discount', 0);

      form.setError('discount_code', {
        type: 'server',
        message:
          ((error as AxiosError)?.response?.data as { error: string })?.error ??
          (error as Error)?.message ??
          'Failed to apply discount. Please try again.',
      });
    }
  };

  const onValidData = async (data: OrderFormData) => {
    await new Promise<void>((resolve) =>
      router.post(route('shoppe.place-order'), data, {
        onError: (errors) => {
          onResult({
            status: 'error',
            message: errors[0] ?? 'Something went wrong. Retry!',
          });
        },
        onSuccess: ({ props }) => {
          if (props.status) {
            onResult({ status: 'success', message: props.status as string });
          }
          onClose(false);
        },
        onFinish: () => {
          resolve();
        },
      })
    );
  };

  return (
    <Drawer
      open={open}
      onOpenChange={(state) => {
        form.reset();
        onClose(state);
      }}
    >
      <DrawerContent className="sm:max-w-sm max-h-screen mx-auto">
        <DrawerHeader>
          <DrawerTitle>Payment Details</DrawerTitle>
          <DrawerDescription>
            Fill the below form to place an order.
          </DrawerDescription>
        </DrawerHeader>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onValidData)}
            className="flex flex-col gap-10 p-4 overflow-auto"
          >
            <div className="flex flex-col gap-4">
              <Typography.Subtle className="font-semibold">
                Scan and Pay
              </Typography.Subtle>

              <NumberField
                control={form.control}
                name="qty"
                label="Order Quantity"
                max={productDetails.available_qty}
              />

              <InputField
                control={form.control}
                name="discount_code"
                label="Discount Code"
                endDecorator={
                  <Button
                    type="button"
                    onClick={handleDiscount}
                    disabled={discountCode === ''}
                  >
                    Apply
                  </Button>
                }
              />

              <Typography.Body>
                Amount to be Paid : ₹{' '}
                <span className="space-x-2">
                  <strong>{afterDiscount}</strong>
                  {beforeDiscount !== afterDiscount && (
                    <span className="text-destructive line-through">
                      {beforeDiscount}
                    </span>
                  )}
                </span>
              </Typography.Body>

              <PaymentMethod />
            </div>
            <div className="grid w-full max-w-sm items-center gap-6">
              <TextareaField
                control={form.control}
                name="address"
                label="Delivery Address"
                placeholder="Enter Delivery Address"
              />

              <InputField
                control={form.control}
                name="pin_code"
                label="Pin Code"
              />

              <InputField
                control={form.control}
                name="transaction_no"
                label="Transaction No."
              />
            </div>
            <Button type="submit" disabled={isSubmitting}>
              Submit
            </Button>
          </form>
        </Form>
      </DrawerContent>
    </Drawer>
  );
};

interface ProductFormData {
  color: number;
  size: number;
}

const ShowProduct = ({ product }: { product: Product }) => {
  const [currentImage, setCurrentImage] = useState(0);

  const form = useForm<ProductFormData>({
    defaultValues: {
      color: 0,
      size: -1,
    },
  });

  const [productDetails, setProductDetails] = useState({
    amount: product.price,
    available_qty: 0,
    inventory_id: 0,
  });

  const [isAvailable, setIsAvailable] = useState(true);

  const [openDialog, setOpenDialog] = useState(false);
  const [openAlert, setOpenAlert] = useState(false);
  const [result, setResult] = useState<{
    status: 'success' | 'error';
    message: string;
  }>();

  const { user } = usePage<PageProps>().props.auth;

  useEffect(() => {
    window.dataLayer?.push({
      event: 'view_item',
      ecommerce: {
        currency: 'INR',
        value: product.price,
        items: [
          {
            item_id: String(product.id),
            item_name: product.name,
            price: product.price,
          },
        ],
      },
    });
    // Fire once per page load, not on every re-render this component goes through.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product.id]);

  const colorField = form.watch('color');

  useEffect(() => {
    setIsAvailable(
      product.variants[colorField].inventory.some(
        (inventory) => inventory.available_quantity > 0
      )
    );
  }, [product, colorField]);

  const onValidData = (data: ProductFormData) => {
    if (!user) {
      router.visit(
        route('login', {
          redirect: window.location.pathname + window.location.search,
        })
      );
      return;
    }

    const inventory = product.variants[data.color].inventory;
    let index = 0;

    try {
      if (data.size === -1) {
        if (
          (inventory.length === 1 && inventory[0].size) ||
          inventory.length > 1
        ) {
          throw new Error('Please select a size');
        }
      } else {
        index = data.size;
      }

      form.clearErrors('size');

      setProductDetails({
        amount: product.price,
        available_qty: inventory[index].available_quantity,
        inventory_id: inventory[index].id,
      });
      window.dataLayer?.push({
        event: 'begin_checkout',
        ecommerce: {
          currency: 'INR',
          value: product.price,
          items: [
            {
              item_id: String(product.id),
              item_name: product.name,
              price: product.price,
            },
          ],
        },
      });
      setOpenDialog(true);
    } catch (err) {
      form.setError('size', {
        message: (err as Error).message,
        type: 'required',
      });
    }
  };

  return (
    <>
      <ProductPayment
        open={openDialog}
        onClose={setOpenDialog}
        onResult={(data) => {
          if (data.status === 'success') {
            form.reset();
          }
          setResult(data);
          setOpenAlert(true);
        }}
        productDetails={productDetails}
      />

      <AlertMessage
        open={openAlert}
        onClose={setOpenAlert}
        status={result?.status}
        message={result?.message}
      />

      <Band tone="surface">
        <SectionLayout space="lg">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 md:items-start md:gap-10">
            {/*
              Gallery: a vertical thumbnail rail beside the main image from md,
              a horizontal strip beneath it on a phone.

              The thumbnails were CarouselItems that swapped the main image on
              onMouseOver alone, so on a touch screen there was no way to see
              any picture but the first, and no way at all by keyboard. They are
              buttons now.

              items-start on the grid, not self-start on this column: a grid
              item stretches by default, and a stretched item has no room to
              stick inside. top-24 clears the 72px header.
            */}
            <div className="md:sticky md:top-24">
              <div className="flex flex-col-reverse gap-3 md:flex-row">
                <div className="flex gap-3 overflow-x-auto pb-1 md:w-20 md:flex-col md:overflow-visible md:pb-0">
                  {product.variants[colorField].images.map((image, index) => (
                    <button
                      type="button"
                      key={index}
                      onClick={() => setCurrentImage(index)}
                      onMouseOver={() => setCurrentImage(index)}
                      onFocus={() => setCurrentImage(index)}
                      aria-label={`Show image ${index + 1} of ${product.name}`}
                      aria-current={index === currentImage}
                      className={cn(
                        'aspect-square w-16 shrink-0 overflow-hidden rounded-lg border-2 bg-muted transition-colors duration-200 md:w-full',
                        index === currentImage
                          ? 'border-primary'
                          : 'border-transparent hover:border-thread'
                      )}
                    >
                      <img
                        src={`/images/shoppe/products/${product.slug}/${image}`}
                        loading="lazy"
                        alt=""
                        width={1280}
                        height={1280}
                        className="size-full object-cover"
                      />
                    </button>
                  ))}
                </div>

                <div className="flex-1 overflow-hidden rounded-xl bg-muted">
                  <img
                    src={`/images/shoppe/products/${product.slug}/${product.variants[colorField].images[currentImage]}`}
                    loading="eager"
                    alt={product.name}
                    width={1280}
                    height={1280}
                    className="aspect-square w-full object-cover"
                  />
                </div>
              </div>
            </div>
            {/* <div className="hidden sm:grid grid-cols-2">
              {product.variants[data['color']].images.map((image, index) => (
                <img
                  key={index}
                  src={`/images/shoppe/products/${image}`}
                  loading="lazy"
                  alt={product.name}
                />
              ))}
            </div> */}
            <div className="flex flex-col gap-6 md:mx-6">
              <div className="pt-6 flex flex-col items-start gap-2">
                {/* Display1 renders an <h1>; the sizing matches the previous Display3. */}
                <Typography.Display1 className="text-4xl lg:text-5xl">
                  {product.name}
                </Typography.Display1>
                <Typography.Subtle className="text-xs">
                  SKU: {product.code}
                </Typography.Subtle>
                {/* <Typography.Subtle className="mt-4 p-1 inline-flex items-center justify-center gap-1 border">
                  {CalculateAvgRating(
                    product.rating_count,
                    product.review_count
                  )}{' '}
                  <StarFilledIcon className="size-4 text-primary" /> (
                  {formatReviewCount(product.review_count)} reviews)
                </Typography.Subtle> */}
              </div>
              <div>
                <div className="py-1 inline-flex items-center gap-2">
                  {/* Display3 only for its size — a price isn't a section
                      heading, so render it as a plain paragraph instead of
                      the h3 it'd otherwise skip down to from the h1 above. */}
                  <Typography.Display3 as="p">
                    &#8377; {product.price}
                  </Typography.Display3>
                  {product.actual_price &&
                    product.actual_price > product.price && (
                      <>
                        <Typography.Body className="line-through decoration-1">
                          &#8377; {product.actual_price}
                        </Typography.Body>
                        <Typography.Subtle className="text-primary">
                          (
                          {calculatePercentOff(
                            product.actual_price,
                            product.price
                          )}{' '}
                          off)
                        </Typography.Subtle>
                      </>
                    )}
                </div>
                <Typography.Body className="text-primary font-medium">
                  Inclusive of all taxes
                </Typography.Body>
              </div>

              <Form {...form}>
                <form
                  onSubmit={form.handleSubmit(onValidData)}
                  className="pt-4 flex flex-col gap-y-6"
                  noValidate
                >
                  <div className="flex flex-col gap-6">
                    {product.variants[0].color && (
                      <FormField
                        control={form.control}
                        name="color"
                        render={({ field: { value, onChange, ...field } }) => (
                          <FormItem>
                            <fieldset className="m-0 space-y-2 border-0 p-0">
                              <legend className="mb-4 text-sm font-medium leading-none">
                                Colors
                              </legend>
                              <FormControl>
                                <RadioGroup
                                  className="flex flex-wrap items-start gap-2"
                                  value={value.toString()}
                                  onValueChange={(option) => {
                                    onChange(parseInt(option));
                                    form.setValue('size', -1);
                                    setCurrentImage(0);
                                  }}
                                  {...field}
                                >
                                  {product.variants.map((variant, index) => (
                                    <FormItem
                                      key={index}
                                      className="basis-auto flex items-center space-y-0"
                                      title={variant.color!.name}
                                    >
                                      <FormControl>
                                        <RadioGroupItem
                                          value={index.toString()}
                                          className="peer sr-only"
                                        />
                                      </FormControl>
                                      <FormLabel className="p-0.5 flex items-center justify-center rounded-md border-2 border-muted bg-popover hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary has-data-[state=checked]:border-primary">
                                        <img
                                          className="rounded-md size-20 object-cover"
                                          src={`/images/shoppe/products/${product.slug}/${variant.images[0]}`}
                                          alt={variant.color!.name}
                                          width={1280}
                                          height={1280}
                                          loading="lazy"
                                        />
                                      </FormLabel>
                                    </FormItem>
                                  ))}
                                </RadioGroup>
                              </FormControl>
                            </fieldset>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    )}

                    {product.variants[colorField].inventory[0].size ? (
                      <FormField
                        control={form.control}
                        name="size"
                        render={({ field: { value, onChange, ...field } }) => (
                          <FormItem>
                            <fieldset className="m-0 space-y-2 border-0 p-0">
                              <legend className="mb-4 text-sm font-medium leading-none">
                                Select Size
                              </legend>
                              <FormControl>
                                <RadioGroup
                                  className="flex flex-wrap items-start gap-2"
                                  value={value.toString()}
                                  onValueChange={(option) =>
                                    onChange(parseInt(option))
                                  }
                                  {...field}
                                >
                                  {product.variants[colorField].inventory.map(
                                    (inventory, index) => (
                                      <FormItem
                                        key={index}
                                        className="flex items-center space-y-0"
                                        title={inventory.size!.value}
                                      >
                                        <FormControl>
                                          <RadioGroupItem
                                            value={index.toString()}
                                            className="peer sr-only"
                                            disabled={
                                              inventory.available_quantity === 0
                                            }
                                          />
                                        </FormControl>
                                        <FormLabel className="relative p-2 min-w-12 h-12 flex items-center justify-center rounded-md border-2 border-muted bg-popover hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary has-data-[state=checked]:border-primary">
                                          <>
                                            {inventory.size!.name}
                                            {inventory.available_quantity < 5 &&
                                              inventory.available_quantity >
                                                0 && (
                                                <Typography.Small className="absolute -bottom-2 px-1 bg-primary-muted text-center text-primary rounded-md">
                                                  {inventory.available_quantity}{' '}
                                                  left
                                                </Typography.Small>
                                              )}
                                            {inventory.available_quantity ===
                                              0 && (
                                              <span
                                                className="absolute inset-0 bg-secondary-foreground/20 rounded"
                                                style={{
                                                  clipPath:
                                                    'polygon(100% 0, 100% 4px, 0% 100%, 0% calc(100% - 4px))',
                                                }}
                                              />
                                            )}
                                          </>
                                        </FormLabel>
                                      </FormItem>
                                    )
                                  )}
                                </RadioGroup>
                              </FormControl>
                            </fieldset>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    ) : (
                      <InventoryStatus
                        inventory={product.variants[colorField].inventory}
                      />
                    )}
                  </div>
                  {!isAvailable && (
                    <Typography.Body className="font-medium">
                      Sold Out
                    </Typography.Body>
                  )}

                  <div className="w-fit p-2 bg-primary-muted inline-flex items-center justify-center">
                    <Boxes size={24} strokeWidth={1} className="mr-3" />
                    <Typography.Subtle className="text-secondary-foreground">
                      For Bulk Orders, please{' '}
                      <Link
                        href={route('contact')}
                        className="underline underline-offset-4 decoration-1 hover:text-primary"
                      >
                        contact
                      </Link>{' '}
                      our sales team
                    </Typography.Subtle>
                  </div>

                  <div className="pt-4 flex gap-4">
                    <Button
                      type="submit"
                      className="flex-1 md:flex-none"
                      disabled={!isAvailable}
                    >
                      Buy Now
                    </Button>
                    <ShareDialog
                      url={window.location.href}
                      title={product.name}
                      content={(JSON.parse(product.details) as string[])[0]}
                    />
                  </div>
                </form>
              </Form>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-10 pt-6 md:grid-cols-2">
            {product.details && product.details.length > 4 && (
              <div className="flex flex-col gap-y-4">
                {/* h2, not H3's default h3: the product name above is this
                    page's h1, and the price just above isn't a heading at
                    all (see the as="p" override), so nothing else sits
                    between the h1 and this section heading. */}
                <Typography.H3 as="h2">Product Details</Typography.H3>
                <div className="grid grid-cols-1  gap-6">
                  {(
                    JSON.parse(product.details) as (
                      string | Record<string, string>
                    )[]
                  ).map((info, index) => {
                    return typeof info === 'string' ? (
                      <Typography.Subtle key={index}>{info}</Typography.Subtle>
                    ) : (
                      <div key={index} className="flex flex-col gap-2">
                        <Typography.Body key={index} className="font-semibold">
                          {info.title}
                        </Typography.Body>
                        <div className="space-y-1">
                          {info.content &&
                            (Array.isArray(info.content) ? (
                              (info.content as string[]).map(
                                (content, index) => (
                                  <Typography.Subtle key={index}>
                                    {content}
                                  </Typography.Subtle>
                                )
                              )
                            ) : (
                              <div className="grid grid-cols-2 md:max-w-96 gap-2">
                                {Object.keys(
                                  info.content as unknown as Record<
                                    string,
                                    string
                                  >
                                ).map((row, index) => (
                                  <div
                                    key={index}
                                    className="p-2 flex flex-col border-b"
                                  >
                                    <Typography.Subtle>{row}</Typography.Subtle>
                                    <Typography.Subtle className="font-medium text-secondary-foreground">
                                      {
                                        (
                                          info.content as unknown as Record<
                                            string,
                                            string
                                          >
                                        )[row]
                                      }
                                    </Typography.Subtle>
                                  </div>
                                ))}
                              </div>
                            ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/*
              These were two stock rubber-stamp PNGs — one black, one navy —
              in a palette and an icon language the site uses nowhere else.
              Same two claims, carried by the chip treatment the contact page
              already uses, so they read as part of the product rather than as
              clipart pasted underneath it.
            */}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {[
                { icon: Truck, label: 'Free delivery' },
                { icon: ShieldCheck, label: 'Best quality guaranteed' },
              ].map((item) => (
                <div
                  key={item.label}
                  className="flex items-center gap-3 rounded-xl border bg-card p-3"
                >
                  <span className="inline-flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary-muted">
                    <item.icon size={18} className="text-primary" />
                  </span>
                  <Typography.Small className="font-semibold text-foreground">
                    {item.label}
                  </Typography.Small>
                </div>
              ))}
            </div>
          </div>
        </SectionLayout>
      </Band>
    </>
  );
};

export default ShowProduct;
